import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import Select from "react-select";
import FieldIcon from "../utils/Register/field_icon";
import SendBtn from "../utils/Register/send_btn";
import PartnerPopUp from "../utils/tournament/partners";
import CircleTournamentAvatar from "../utils/tournament/tournament_profile_img";
import { showToast } from "./ToastNotification";
import CategoryPopup from "../utils/tournament/categoryPopup";

const CreateTeamPopup = ({
  tournament,
  selectedSubcategory,
  onClose,
  onTeamCreated,
}) => {
  const [teamName, setTeamName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [error, setError] = useState("");
  const [image, setImage] = useState(null);
  const [userId, setUserId] = useState("");
  const [partners, setPartners] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [showPartnerPopUp, setShowPartnerPopUp] = useState(false);

  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [showSubcategoryPopup, setShowSubcategoryPopup] = useState(false);

  const token = useSelector((state) => state.user.token);

  const optionsInf = [
    { label: "Sí", value: "Sí" },
    { label: "No", value: "No" },
  ];

  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.id);
    }
  }, [token]);

  useEffect(() => {
    const fetchPartners = async () => {
      if (!token) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/partners`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok)
          throw new Error("Error al obtener los patrocinadores.");

        const data = await response.json();
        setPartners(
          data.map((partner) => ({ label: partner.name, value: partner.id })),
        );
      } catch (error) {
        console.error("Error:", error.message);
      }
    };

    fetchPartners();
  }, [token]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
    }
  };

  const handleAddPartner = (newPartner) => {
    setPartners((prev) => [
      ...prev,
      { label: newPartner.name, value: newPartner.id },
    ]);
    setSelectedPartners((prev) => [
      ...prev,
      { label: newPartner.name, value: newPartner.id },
    ]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!teamName || !city) {
      setError("Por favor, complete el nombre del equipo y la ciudad.");
      return;
    }

    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("team_name", teamName);
    formData.append("phone", phone || "");
    formData.append("city", city);

    let subcategoriesToSend = selectedSubcategories;
    if (!selectedSubcategories || selectedSubcategories.length === 0) {
      subcategoriesToSend = [
        {
          number: 0,
          group_name: "principal",
        },
      ];
    }

    formData.append("subcategories", JSON.stringify(subcategoriesToSend));

    selectedPartners.forEach((partner) => {
      formData.append("partners[]", partner.value);
    });

    if (image) {
      formData.append("picture_url", image);
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/teams`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Error al crear el equipo.");
      }

      // **Registrar el equipo en el torneo con o sin subcategoría**
      const requestBody = {
        subcategoryId: selectedSubcategory ? selectedSubcategory : null,
      };

      const registerResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tournaments/${tournament.id}/teams/${data.id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!registerResponse.ok) {
        throw new Error("Error al registrar el equipo en el torneo.");
      }

      onTeamCreated();
      onClose();
      showToast.success("Equipo creado con éxito!");
    } catch (error) {
      setError(error.message);
      showToast.error("Error al crear el equipo.", error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="relative bg-white p-8 rounded-lg w-96">
        {/* Botón de cierre "X" */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-lg font-bold"
        >
          ✖
        </button>

        {error && <p className="text-red-500 text-center mt-2">{error}</p>}
        <form onSubmit={handleSubmit}>
          <div className="flex justify-center">
            <CircleTournamentAvatar
              size={100}
              onImageSelect={handleImageChange}
            />
          </div>
          <FieldIcon
            id="teamName"
            name="teamName"
            type="text"
            autoComplete="on"
            placeholder="Nombre del equipo"
            value={teamName}
            required
            label="Nombre del equipo *"
            onChange={(e) => setTeamName(e.target.value)}
          />
          <FieldIcon
            id="phone"
            name="phone"
            type="text"
            autoComplete="on"
            placeholder="Teléfono"
            value={phone}
            label="Teléfono (opcional)"
            onChange={(e) => setPhone(e.target.value)}
          />
          <FieldIcon
            id="city"
            name="city"
            type="text"
            autoComplete="on"
            placeholder="Ciudad"
            value={city}
            required
            label="Ciudad *"
            onChange={(e) => setCity(e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium mb-2">
              Subcategorías
            </label>
            <button
              type="button"
              className="bg-blue-500 text-white px-3 py-1 rounded-lg mb-2"
              onClick={() => setShowSubcategoryPopup(true)}
            >
              Configurar Subcategorías
            </button>

            {showSubcategoryPopup && (
              <CategoryPopup
                onClose={() => setShowSubcategoryPopup(false)}
                onSave={(subs) => {
                  setSelectedSubcategories(subs);
                  setShowSubcategoryPopup(false);
                }}
                initialSubcategories={selectedSubcategories}
              />
            )}

            {selectedSubcategories.length > 0 && (
              <p className="mt-2 text-gray-700">
                Subcategorías seleccionadas:{" "}
                {selectedSubcategories
                  .map((sub) =>
                    sub.number && sub.group_name
                      ? `${sub.number} ${sub.group_name}`
                      : sub.number
                        ? sub.number
                        : sub.group_name,
                  )
                  .join(", ")}
              </p>
            )}
          </div>
          <label className="block text-sm font-medium mb-2">
            Patrocinadores
          </label>
          <Select
            options={partners}
            isMulti
            value={selectedPartners}
            onChange={setSelectedPartners}
            placeholder="Selecciona patrocinadores"
            className="mb-4"
          />
          <button
            type="button"
            className="text-blue-500 text-sm underline mb-4"
            onClick={() => setShowPartnerPopUp(true)}
          >
            Crear nuevo patrocinador
          </button>
          <SendBtn
            label="Guardar"
            type="submit"
            style={{ backgroundColor: "#E5960E" }}
          />
        </form>
        {showPartnerPopUp && (
          <PartnerPopUp
            onClose={() => setShowPartnerPopUp(false)}
            onAddPartner={handleAddPartner}
          />
        )}
      </div>
    </div>
  );
};

export default CreateTeamPopup;
